
import { useEffect, useState } from "react";
import { IconButton, CircularProgress } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import Select from "react-select";
import { axiosInstance } from "../../../utility/apiInstance";
import toast from "react-hot-toast";

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "38px",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#F8B71F" : "#e2e2e2",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(248,183,31,0.18)" : "none",
    "&:hover": { borderColor: "#F8B71F" },
    fontSize: "12px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: "12px",
    background: state.isSelected ? "#F8B71F" : state.isFocused ? "#fef9c3" : "#fff",
    color: state.isSelected ? "#1a1a1a" : "#374151",
  }),
};

function TabCustomMessage({ item }: { item: any }) {
  const [contactOptions, setContactOptions] = useState<any[]>([]);
  const [channelOptions] = useState<any[]>([
    { label: "WhatsApp", value: "whatsapp" },
    { label: "Email", value: "email" },
  ]);

  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [selectedChannel, setSelectedChannel] = useState<any>(null);
  const [messageText, setMessageText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [fileKey, setFileKey] = useState<string>("");
  const [messages, setMessages] = useState<
    {
      _id: string;
      text: string;
      contact: string;
      channel: string;
      attachment?: string;
      createdAt: string;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const { data } = await axiosInstance.post("/api/v1/s3/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / (event.total || 1));
          setUploadProgress(percent);
        },
      });

      const uploadedUrl = data.data.url || "";
      const uploadedFileKey = data.data.key || "";
      setFileUrl(uploadedUrl);
      setFileKey(uploadedFileKey);
      toast.success("File uploaded successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "File upload failed");
      setFile(null);
      setFileUrl("");
      setFileKey("");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteFile = async () => {
    if (!fileKey) return;
    try {
      await axiosInstance.post(`/api/v1/s3/delete`, { fileKey });
      setFile(null);
      setFileUrl("");
      setFileKey("");
      toast.success("File deleted successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to delete file");
    }
  };

  const fetchRecipients = async () => {
    if (!selectedChannel?.value || !item?.mawb) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.post(`/api/v1/subscription/get-recipients`, {
        alertType: selectedChannel?.value,
        trackingNumber: item?.mawb,
        shippingType: item["for"],
      });
      const recipients = response.data?.data || [];
      setContactOptions(recipients.map((r: any) => ({ label: `${r.salutation} ${r.name} (${selectedChannel?.value=="email"?r.email:r.whatappNumber},${r?.masterContactOrganization?.name||""})`, value: r })));
    } catch (e: any) {
      toast.error("Failed to fetch recipients");
      setContactOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients();
  }, [selectedChannel, item?.mawb]);

  const fetchMessages = async () => {
    if (!item?.mawb) return;
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(`/api/v1/chat/${item.mawb}`);
      setMessages(data?.data || []);
    } catch (e: any) {
      const errorMsg =
        e.response?.data?.message || e.response?.data?.error || "Failed to fetch messages";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [item?.mawb]);

  const handleSend = async () => {
    if (!selectedContact || !selectedChannel) {
      toast.error("Please select contact and channel");
      return;
    }
    if (!messageText.trim()) {
      toast.error("Please write in box ");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        text: messageText,
        contact: selectedContact.value,
        trackingNumber: item?.mawb,
        channel: selectedChannel?.value,
        attachment: fileUrl,
        shippingType: item["for"],
      };

      const { data } = await axiosInstance.post("/api/v1/chat/send", payload);
      toast.success(data?.message || "Message sent successfully");

      setMessageText("");
      setFile(null);
      setFileUrl("");
      setFileKey("");
      setSelectedContact(null);
      setSelectedChannel(null);
      setContactOptions([]);
      fetchMessages();
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.message || err.response?.data?.error || "Failed to send message";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="h-[250px] overflow-y-auto p-2 bg-gray-50">
        {loading && (
          <div className="flex justify-center my-2">
            <CircularProgress size={24} />
          </div>
        )}

        {messages.length === 0 && !loading && (
          <div className="text-gray-400 text-sm h-full flex justify-center items-center">
            No messages yet
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className="flex flex-col items-end mb-3">
            <div className="w-[240px] rounded-lg overflow-hidden shadow-sm border border-green-200 bg-green-50">
              <div className="bg-green-500 text-white text-xs font-medium px-2 py-1 flex justify-between">
                <span>{msg.contact}</span>
                <span className="opacity-80">{new Date(msg.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="px-3 py-2 text-sm text-gray-800">
                <p>{msg.text}</p>
                {msg.attachment && (
                  <a
                    href={msg.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-xs underline mt-1 inline-block"
                  >
                    View Attachment
                  </a>
                )}
                <p className="text-[10px] text-gray-500 mt-1 italic">Channel: {msg.channel}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {uploading && (
        <p className="text-blue-600 text-xs mt-1 px-3">Uploading: {uploadProgress}%</p>
      )}

      <div className="border-t border-gray-100 bg-white p-3 grid gap-2.5">
        <div className="grid md:grid-cols-2 gap-2">
          <Select
            name="channel"
            placeholder="Select Channel"
            options={channelOptions}
            value={selectedChannel}
            onChange={(selected) => {
              setSelectedContact(null);
              setSelectedChannel(selected);

            }}
            styles={selectStyles}
          />

          <Select
            name="contact"
            placeholder="Select Contact"
            options={contactOptions}
            value={selectedContact}
            onChange={(selected) => setSelectedContact(selected)}
            isDisabled={!selectedChannel}
            styles={selectStyles}
          />
        </div>

        <div className="relative">
          <textarea
            className="w-full min-h-[84px] max-h-[120px] resize-y rounded-lg border border-gray-300 p-3 pr-24 text-sm outline-none focus:border-[#F8B71F] focus:ring-2 focus:ring-[#F8B71F]/20"
            placeholder="Enter message"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            {selectedChannel?.value === "email" && (
              <IconButton component="label" disabled={uploading} size="small">
                <AttachFileIcon fontSize="small" />
                <input type="file" hidden onChange={handleFileChange} />
              </IconButton>
            )}
            {fileUrl && (
              <button
                type="button"
                onClick={handleDeleteFile}
                className="text-xs text-red-600 hover:text-red-700 px-2 py-1"
              >
                Delete
              </button>
            )}
            <IconButton onClick={handleSend} color="primary" disabled={uploading || loading} size="small">
              <SendIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TabCustomMessage;